import isNil from "lodash/isNil";

interface LegalDocumentType {
  date: string;
  key: string;
  name: string;
  version: string;
}

interface ConsentRequest {
  consentName: string;
  consentVersion: string;
}

enum LegalDocuments {
  PrivacyPolicy = "Privacy Policy",
  TermsOfUse = "Terms of Use",
}

const LegalDocumentDetails: Record<LegalDocuments, LegalDocumentType> = {
  [LegalDocuments.PrivacyPolicy]: {
    date: "2025-02-01",
    key: "Privacy policy",
    name: LegalDocuments.PrivacyPolicy,
    version: "2025.02.01",
  },
  [LegalDocuments.TermsOfUse]: {
    date: "2025-13-01",
    key: "Terms of use",
    name: LegalDocuments.TermsOfUse,
    version: "2025.13.01",
  },
};

const getAllLegalDocuments = (): LegalDocumentType[] => {
  return Object.values(LegalDocumentDetails);
};

/**
 * Checks if the user has already consented to each legal document.
 * @param consents - Array of consents the user has already agreed to
 * @returns An object with the document name as key and boolean indicating if consent exists
 */
const getIsLegalDocumentConsented = (
  consents: Consent[] = [],
): Record<string, boolean> => {
  if (isNil(consents)) {
    return getAllLegalDocuments().reduce(
      // eslint-disable-next-line max-params -- reduce needs accumulator and current value
      (result, doc) => ({ ...result, [doc.name]: false }),
      {},
    );
  }

  return getAllLegalDocuments().reduce(
    // eslint-disable-next-line max-params -- reduce needs accumulator and current value
    (result, doc) => {
      const hasConsent = consents.some(
        (consent) =>
          consent.consentName === doc.key &&
          consent.consentVersion === doc.version,
      );
      return { ...result, [doc.name]: hasConsent };
    },
    {},
  );
};

const getPendingLegalDocuments = (consents?: Consent[]): ConsentRequest[] => {
  if (isNil(consents) || consents.length === 0) {
    return getAllLegalDocuments().map((doc) => ({
      consentName: doc.key,
      consentVersion: doc.version,
    }));
  }

  const pendingLegalDocuments = getAllLegalDocuments().reduce<ConsentRequest[]>(
    // eslint-disable-next-line max-params -- this is a complex function
    (prev, currentLegalDoc) => {
      const consentedLegalDocument = consents.find(
        (consent) =>
          consent.consentName === currentLegalDoc.key &&
          consent.consentVersion === currentLegalDoc.version,
      );

      if (consentedLegalDocument === undefined) {
        return [
          ...prev,
          {
            consentName: currentLegalDoc.key,
            consentVersion: currentLegalDoc.version,
          },
        ];
      }
      return [...prev];
    },
    [],
  );

  return pendingLegalDocuments;
};

export type { ConsentRequest };
export {
  getAllLegalDocuments,
  getIsLegalDocumentConsented,
  getPendingLegalDocuments,
  LegalDocumentDetails,
  LegalDocuments,
};
